import {createSlice} from '@reduxjs/toolkit';
import {omit, without} from 'lodash-es';
import {displayDateFormat} from 'utils/date-time';
import dayjs from 'utils/dayjs';

const initialDateFrom = dayjs()
    .subtract(1, 'month')
    .startOf('month')
    .format(displayDateFormat);

const initialDateTo = dayjs()
    .subtract(1, 'month')
    .endOf('month')
    .format(displayDateFormat);

/** @namespace Reports.Actions */

/**
 * @typedef {object} TReportsSliceInitialState
 * @property {object} filters
 * @property {string} filters.dateFrom - The start date for the report in the format MM/DD/YYYY.  Defaults to the start of the previous month
 * @property {string} filters.dateTo - The end date for the report in the format MM/DD/YYYY.  Defaults to the end of the previous month
 * @property {('all'|'transient'|'monthly')} filters.reservationType - The type of reservation to include in the report.  Defaults to 'all'
 * @property {number} filters.numFiltersApplied - The number of filters that have been applied.  Defaults to 0
 */
export const initialState = {
    filters: {
        dateFrom: initialDateFrom,
        dateTo: initialDateTo,
        reservationType: 'all',
        numFiltersApplied: 0,
    },
};

const reportsSlice = createSlice({
    name: 'reports',
    initialState,
    reducers: {
        /**
         * Updates the filters for the reports
         * @memberof Reports.Actions
         * @param {TReportsSliceInitialState} [state] - The current state
         * @param payload - The new filters
         * @param payload.dateFrom - The start date for the report in the format MM/DD/YYYY.
         * @param payload.dateTo - The end date for the report in the format MM/DD/YYYY.
         * @param payload.reservationType - The type of reservation to include in the report.
         * @returns {dateFrom: string, dateTo: string, reservationType: string, numFiltersApplied: number}
         *
         */
        updateFilters: {
            reducer: (state, {payload}) => {
                state.filters = payload;
            },
            prepare: (payload) => {
                let appliedFiltersCount = 0;

                const initialFilters = omit(
                    initialState.filters,
                    'numFiltersApplied'
                );

                const appliedFilters = without(
                    Object.values(payload),
                    ...Object.values(initialFilters)
                );

                appliedFilters.forEach((filter) => {
                    if (
                        Array.isArray(filter)
                            ? filter.length > 0
                            : Boolean(filter)
                    ) {
                        appliedFiltersCount++;
                    }
                });

                return {
                    payload: {
                        ...payload,
                        numFiltersApplied: appliedFiltersCount,
                    },
                };
            },
        },
    },
});

export const {updateFilters} = reportsSlice.actions;
export default reportsSlice.reducer;
